import type { DailyStat, DonationForm } from '@feathr/blackbox';
import type { ListResponse } from '@feathr/rachis';

import { useStore } from '../../../state';
import type { TDateRange } from '../DonationFormReport.utils';

export interface IUseDonationFormTrendsProps {
  /** The donation form being reported on. */
  form: DonationForm;
  /** The resolved `[startISO, endISO]` range for the current window. */
  range: TDateRange;
  /** The resolved `[startISO, endISO]` range for the immediately preceding window. */
  previousRange: TDateRange;
}

export interface IUseDonationFormTrendsReturn {
  currentDailyStats: ListResponse<DailyStat>;
  previousDailyStats: ListResponse<DailyStat>;
  isLoading: boolean;
}

const DAILY_STATS_PAGE_SIZE = 100;

/**
 * Fetches per-day stats for a donation form for both the current and the
 * immediately-preceding window. Mirrors `CampaignReport`'s `Stats.list` pattern
 * but against `model: 'form'` — the generic `/stats/?model=form` endpoint
 * returns daily documents with the donation-form-specific fields captured by
 * `IFormDailyStats`.
 */
export function useDonationFormTrends({
  form,
  range,
  previousRange,
}: IUseDonationFormTrendsProps): IUseDonationFormTrendsReturn {
  const store = useStore();

  const currentDailyStats = store.Stats.list({
    filters: {
      metadata__obj_id: form.id,
      metadata__date__gte: range[0],
      metadata__date__lte: range[1],
    },
    model: 'form',
    ordering: ['metadata.date'],
    pagination: { page_size: DAILY_STATS_PAGE_SIZE },
  });

  const previousDailyStats = store.Stats.list({
    filters: {
      metadata__obj_id: form.id,
      metadata__date__gte: previousRange[0],
      metadata__date__lte: previousRange[1],
    },
    model: 'form',
    ordering: ['metadata.date'],
    pagination: { page_size: DAILY_STATS_PAGE_SIZE },
  });

  const isLoading = currentDailyStats.isPending || previousDailyStats.isPending;

  return {
    currentDailyStats,
    previousDailyStats,
    isLoading,
  };
}
